import ParagraphText from "@/components/ParagraphText";
import { TProductInfo } from "@/typings";
import { Button, Card, Grid, Rate } from "@arco-design/web-react";
import React, { useCallback, useContext, useState } from "react";
import Image from "next/image";
import { useMeasure } from "react-use";
import { animated } from "@react-spring/web";
import { LoginContext } from "@/app/LoginProvider";
import LoginModal from "@/app/(routeLayout)/login/(components)/LoginModal";
import {
  TrackingEventPageID,
  TrackingEventID,
  TrackingEventType,
  RouteStack,
  ProductCardType,
  TrackingBtnId,
} from "@/shared";
import ClientLink from "@/components/ClientLink";
import { ChristmasIconSrc } from "@/cdn-resources/christmas";
import { useTrackingRecordSave } from "@/api/track/use-query";
import ProductSkeleton from "@/components/ProductSkeleton";
import "./index.scss";
import useAddShowcase, { EventConfig } from "@/hooks/useAddShowcase";
import { Icon } from "../Icons";
import EvokeSkuAction from "../EvokeSkuAction";

const { Meta } = Card;
const { Row, Col } = Grid;

const LAYOUT_COL_CARD = {
  xs: { span: 24 },
  sm: { span: 12 },
  md: { span: 8 },
  lg: { span: 6 },
  xl: { span: 6 },
  xxl: { span: 4 },
  xxxl: { span: 4 },
};

interface GeneralProductCardProps {
  product: TProductInfo;
  type?: ProductCardType;
  eventConfig?: EventConfig;
}

const GeneralProductCard: React.FC<GeneralProductCardProps> = ({
  product,
  type,
  eventConfig,
}) => {
  const { mutate: fetchTrackingRecordSave } = useTrackingRecordSave();

  const { checkShowcaseAuthorize, loading, isAddedShowcase } = useAddShowcase({
    productData: product,
    eventConfig: {
      btn_id: TrackingBtnId.AddShowCase,
      ...eventConfig,
    },
  });

  const [ref, { width }] = useMeasure();

  const loginCtx = useContext(LoginContext);
  const [loginVisible, $loginVisible] = useState<boolean>(false);

  const trackProductCard = useCallback(() => {
    fetchTrackingRecordSave({
      uid: loginCtx?.userInfo?.uid,
      event_id: TrackingEventID.ProductCard,
      event_type: TrackingEventType.Click,
      page_id: TrackingEventPageID.Home,
      product_id: product?.product_id,
    });
  }, [fetchTrackingRecordSave, product?.product_id, loginCtx?.userInfo?.uid]);

  const addShowcase = useCallback(
    (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      checkShowcaseAuthorize();
    },
    [checkShowcaseAuthorize]
  );

  const productDescription = (type?: ProductCardType) => {
    switch (type) {
      case ProductCardType.BestSelling:
        return (
          <>
            <div className="descriptions">
              <div className="yf-best-number">
                <Icon.ProductFire />
                <div className="yf-price-value">
                  <ParagraphText text={product?.day28_sales_count_str || "-"} />
                </div>
                <span className="yf-best-title">28d sold</span>
              </div>
              <div className="yf-best-creator">
                <ParagraphText>
                  <span className="price-value">
                    {product?.influencers_count_str || "-"}
                  </span>
                  <span>creators</span>
                </ParagraphText>
              </div>
            </div>
            <Row className="descriptions">
              <Col span={12} className="descriptions-item">
                <span className="descriptions-item-label">Price:</span>
                <ParagraphText
                  className="price-value"
                  text={product.promotion_lower_price_str}
                />
              </Col>
              <Col span={12} className="descriptions-item last">
                <span className="descriptions-item-label">Earn:</span>
                <ParagraphText
                  className="price-value"
                  text={[product.est_creator_commission_amount_str]}
                />
              </Col>
            </Row>
            <div className="vs-content h-[60px]">
              <div className="right-best-selling">
                <div className="content-hull">
                  <div className="content">
                    <span className="commission-rate">
                      {product?.plan_commission_rate_str}
                    </span>
                    <span className="public-commission-label">
                      <ParagraphText text={"Open comm"} />
                    </span>
                  </div>
                </div>
              </div>
              <div className="left-best-selling">
                <div className="commission-rate">
                  <Icon.Medal />
                  {product?.creator_commission_rate_str}
                </div>
                <label className="commission-label">
                  <ParagraphText text={"YooFinds comm"} />
                </label>
              </div>
              <div className="vs-cover-best-selling">vs</div>
            </div>
          </>
        );
      case ProductCardType.HighCommission:
        return (
          <>
            <div className="vs-content h-[70px]">
              <div className="right-high-commission">
                <div className="content-hull">
                  <div className="content">
                    <span className="commission-rate">
                      {product?.plan_commission_rate_str}
                    </span>
                    <span className="public-commission-label">
                      <ParagraphText text={"Open comm"} />
                    </span>
                  </div>
                </div>
              </div>
              <div className="left-high-commission">
                <div className="commission-rate">
                  <Icon.Medal /> {product?.creator_commission_rate_str}
                </div>
                <label className="commission-label">
                  <ParagraphText text={"YooFinds comm"} />
                </label>
              </div>
              <div className="vs-cover-high-commission">vs</div>
            </div>
            <Row className="descriptions">
              <Col span={12} className="descriptions-item">
                <span className="descriptions-item-label">Price: </span>
                <ParagraphText
                  className="price-value"
                  text={product.promotion_lower_price_str}
                />
              </Col>
              <Col span={12} className="descriptions-item last">
                <span className="descriptions-item-label">Earn:</span>
                <ParagraphText
                  text={[product.est_creator_commission_amount_str]}
                />
              </Col>
              <Col span={12} className="descriptions-item">
                <span className="descriptions-item-label">
                  <ParagraphText text="Creators:" />
                </span>
                <ParagraphText
                  className="price-value"
                  text={product?.influencers_count_str || "-"}
                />
              </Col>
              <Col span={12} className="descriptions-item last">
                <span className="descriptions-item-label">
                  <ParagraphText text="28d solds:" />
                </span>
                <ParagraphText
                  className="price-value"
                  text={product?.day28_sales_count_str || "-"}
                />
              </Col>
            </Row>
          </>
        );
      case ProductCardType.YouMayLike:
        return (
          <>
            <div className="vs-content h-[70px]">
              <div className="right">
                <div className="content-hull">
                  <div className="content">
                    <span className="commission-rate">
                      {product?.plan_commission_rate_str}
                    </span>
                    <span className="public-commission-label">
                      <ParagraphText text={"Open comm"} />
                    </span>
                  </div>
                </div>
              </div>
              <div className="left">
                <div className="commission-rate">
                  <Icon.Medal /> {product?.creator_commission_rate_str}
                </div>
                <label className="commission-label">
                  <ParagraphText text={"YooFinds comm"} />
                </label>
              </div>
              <div className="vs-cover">vs</div>
            </div>
            <Row className="descriptions">
              <Col span={12} className="descriptions-item">
                <span className="descriptions-item-label">Price: </span>
                <ParagraphText
                  className="price-value"
                  text={product.promotion_lower_price_str}
                />
              </Col>
              <Col span={12} className="descriptions-item last">
                <span className="descriptions-item-label">Earn:</span>
                <ParagraphText
                  className="earn-value"
                  text={[product.est_creator_commission_amount_str]}
                />
              </Col>
              <Col span={12} className="descriptions-item">
                <span className="descriptions-item-label">
                  <ParagraphText text="Creators:" />
                </span>
                <ParagraphText
                  className="price-value"
                  text={product?.influencers_count_str || "-"}
                />
              </Col>
              <Col span={12} className="descriptions-item last">
                <span className="descriptions-item-label">
                  <ParagraphText text="28d solds:" />
                </span>
                <ParagraphText
                  className="price-value"
                  text={product?.day28_sales_count_str || "-"}
                />
              </Col>
            </Row>
          </>
        );
      default:
        return (
          <>
            <div className="vs-content h-[70px]">
              <div className="right">
                <div className="content-hull">
                  <div className="content">
                    <span className="commission-rate">
                      {product?.plan_commission_rate_str}
                    </span>
                    <span className="public-commission-label">
                      <ParagraphText text={"Open comm"} />
                    </span>
                  </div>
                </div>
              </div>
              <div className="left">
                <div className="commission-rate">
                  <Icon.Medal /> {product?.creator_commission_rate_str}
                </div>
                <label className="commission-label">
                  <ParagraphText text={"YooFinds comm"} />
                </label>
              </div>
              <div className="vs-cover">vs</div>
            </div>
            <Row className="descriptions">
              <Col span={12} className="descriptions-item">
                <span className="descriptions-item-label">Price: </span>
                <ParagraphText
                  className="price-value"
                  text={product.promotion_lower_price_str}
                />
              </Col>
              <Col span={12} className="descriptions-item last">
                <span className="descriptions-item-label">Earn:</span>
                <ParagraphText
                  className="earn-value"
                  text={[product.est_creator_commission_amount_str]}
                />
              </Col>
              <Col span={12} className="descriptions-item">
                <span className="descriptions-item-label">
                  <ParagraphText text="Creators:" />
                </span>
                <ParagraphText
                  className="price-value"
                  text={product?.influencers_count_str || "-"}
                />
              </Col>
              <Col span={12} className="descriptions-item last">
                <span className="descriptions-item-label">
                  <ParagraphText text="28d solds:" />
                </span>
                <ParagraphText
                  className="price-value"
                  text={product?.day28_sales_count_str || "-"}
                />
              </Col>
            </Row>
          </>
        );
    }
  };

  return (
    <>
      <Col className={"h-full yf-product-col"} {...LAYOUT_COL_CARD}>
        {loginVisible && (
          <LoginModal
            visible={loginVisible}
            onCancel={() => $loginVisible(false)}
          />
        )}
        <div className={"product-card-container"}>
          <ClientLink
            className="flex w-full h-full"
            href={`/${RouteStack.Product.product}/${product.hash_id}`}
            target="_blank"
            onClick={trackProductCard}
            title={product.product_name}
            aria-label={product.product_name}
          >
            <animated.div className={"w-full h-full"}>
              <Card
                className={"product-card"}
                bordered={false}
                hoverable={true}
                cover={
                  <div
                    ref={(_ref) => {
                      if (_ref) {
                        ref(_ref);
                      }
                    }}
                    className="shop-cover"
                    style={{ height: width }}
                  >
                    {product.cover ? (
                      <Image
                        alt={product.product_name}
                        src={product.cover}
                        width={0}
                        height={0}
                      />
                    ) : (
                      <ProductSkeleton />
                    )}
                  </div>
                }
                actions={[
                  <span key="1" className="shop-rating">
                    <Rate readonly value={product.shop_rating} count={1} />
                    <span className="rate-total">{product.shop_rating}</span>/ 5
                  </span>,
                ]}
              >
                <Meta
                  avatar={
                    <div className="w-full flex items-center">
                      <Image
                        className="shop-cover-img"
                        src={product.shop_cover}
                        alt={product.shop_name}
                        width={0}
                        height={0}
                      />
                      <ParagraphText
                        className="flex-1"
                        text={product.shop_name}
                      />
                    </div>
                  }
                  title={
                    <div className="flex gap-[10px]">
                      {product.is_christmas_product && (
                        <Image
                          className="shop-cover-img"
                          src={ChristmasIconSrc}
                          alt="christmas"
                          width={65}
                          height={34}
                        />
                      )}
                      <ParagraphText
                        text={product.product_name}
                        lineClamp={2}
                      />
                    </div>
                  }
                  description={productDescription(type)}
                />
                <div className="actions flex justify-center">
                  <EvokeSkuAction product={product} eventConfig={eventConfig}>
                    <Button className={"free-sample"}>
                      <ParagraphText text={"Free Sample"} />
                    </Button>
                  </EvokeSkuAction>
                  <Button
                    className={"add-show-case"}
                    loading={loading}
                    onClick={addShowcase}
                  >
                    <ParagraphText
                      text={isAddedShowcase ? "Added" : "Add"}
                      className="mx-0 my-0 flex items-center"
                    />
                  </Button>
                </div>
              </Card>
            </animated.div>
          </ClientLink>
        </div>
      </Col>
    </>
  );
};

export default GeneralProductCard;
